import { useMutation } from '@apollo/client'
import styled from '@emotion/styled'
import { defineMessages, FormattedMessage } from '@faceup/localization'
import { useParams } from '@faceup/router'
import { Space, Spin, Typography } from '@faceup/ui-base'
import { VerifyVictimEmailResult } from '@faceup/utils'
import { useEffect, useState } from 'react'
import { graphql } from '../__generated__'
import useRegion from '../utils/useRegion'
import PlainLayout from './PlainLayout'

const { Title, Text } = Typography

const mutations = {
  EditIsVerifiedCompanyVictim: graphql(`
    mutation EditIsVerifiedCompanyVictimMutation(
      $input: EditIsVerifiedCompanyVictimInput!
    ) {
      editIsVerifiedCompanyVictim(input: $input) {
        result
      }
    }
  `),
}

const verificationResultTitles = defineMessages(
  'followUp.components.emailVerification',
  Object.values(VerifyVictimEmailResult).map(id => ({ id, defaultMessage: '' }))
)

const verificationResultInfos = defineMessages(
  'followUp.components.emailVerification.verificationResultInfos',
  Object.values(VerifyVictimEmailResult).map(id => ({ id, defaultMessage: '' }))
)

const EmailVerification = () => {
  const [result, setResult] = useState<VerifyVictimEmailResult | null>(null)
  const [wasCalled, setWasCalled] = useState(false)
  const { discoverByToken } = useRegion()
  const { id: token } = useParams<'id'>()

  const [editIsCompanyVerified, { loading }] = useMutation(mutations.EditIsVerifiedCompanyVictim, {
    variables: {
      input: {
        token: token ?? '',
      },
    },
    onCompleted: ({ editIsVerifiedCompanyVictim }) => {
      setResult(editIsVerifiedCompanyVictim?.result ?? VerifyVictimEmailResult.Error)
    },
  })

  useEffect(() => {
    const fetchToken = async () => {
      if (token && discoverByToken && editIsCompanyVerified && !wasCalled) {
        setWasCalled(true)
        await discoverByToken(token)
        await editIsCompanyVerified()
      }
    }
    fetchToken()
  }, [discoverByToken, editIsCompanyVerified, token, wasCalled])

  return (
    <PlainLayout>
      <Wrapper>
        <Space direction='vertical' align='center' size={40}>
          {loading || !wasCalled || result === null ? (
            <Spin />
          ) : (
            <Space direction='vertical' align='center' size='small'>
              <Title level={2} style={{ display: 'inline-block' }}>
                <FormattedMessage message={verificationResultTitles[result]} />
              </Title>
              <Text style={{ fontSize: 16 }}>
                <FormattedMessage message={verificationResultInfos[result]} />
              </Text>
            </Space>
          )}
        </Space>
      </Wrapper>
    </PlainLayout>
  )
}

const Wrapper = styled.div`
  margin: auto;
`

export default EmailVerification
